import type { LocaleFormattingTranslate } from './locale-formatting';

/**
 * FE-80.1 — the card's fallback title, in ONE place.
 *
 * ⚠ This leaf replaces `CardTile`'s `UNTITLED_CARD_NAME` module constant, and
 * the reason it is a function rather than a renamed constant is the whole
 * point: a module constant cannot call `t`. The board-frame slice measured the
 * consequence and deferred it deliberately — `BoardFace.tsx` imported the
 * constant at seventeen sites, several of which build a title and then MATCH a
 * rendered card against it, so routing it for one view alone would make the
 * rendered title and the announced title diverge the first time a catalogue is
 * served, with nothing red anywhere.
 *
 * FOUR authors reached the constant (`CardTile`, `QuickEdit`, `BoardMapView`,
 * `BoardFace`), one more than the third-author threshold `custom-field-type.ts`
 * was extracted on, so the extraction is taken rather than argued again.
 *
 * ⚠ `t` is the REQUIRED first parameter, with no default. That is the same
 * mechanism that surfaced the board-frame slice's date-formatter defect: a
 * defaulted formatter let one caller fall through to a default that looked
 * correct, and only making the parameter required forced `tsc` to enumerate
 * every call site.
 *
 * ⚠ ONE trimming rule, where the call sites had two. The constant was reached
 * through `title.trim() === '' ? UNTITLED : title` in `CardTile`/`QuickEdit`
 * and through `title.trim() || UNTITLED` in `BoardMapView`/`BoardFace` — the
 * second form also STRIPS surrounding whitespace from a non-empty title, so a
 * card called `'  Ship it  '` was announced differently from how it was drawn.
 * This function keeps the rendered form (the original string when it is not
 * blank), because that is the one a person sees on the board and the one the
 * matching sites above compare against.
 *
 * `board.card.untitled` rather than a `primitives.*` leaf: every author is a
 * card surface and the value names a card, not a shared control. It is
 * byte-identical to `myCards.untitledCard`, which is a deliberate refusal to
 * share under the settled rule — the two can be on screen at once (`/my/cards`
 * renders neither of these components) only in the sense that they are
 * different surfaces with different owners, and `myCards.*` is a route
 * namespace this leaf must not reach into.
 */
export function cardDisplayTitle(t: LocaleFormattingTranslate, title: string): string {
  return title.trim() === '' ? t('board.card.untitled') : title;
}
